'use client'

import type { PropsWithStyles } from '@morpho-org/ui-v6/types'

import { cn } from '@morpho-org/ui-v6/utils'
import { memo, type PropsWithChildren } from 'react'

export interface SectionLayoutProps extends PropsWithChildren, PropsWithStyles {
  hasLargerVerticalPadding?: boolean
  withoutAside?: boolean
  withOverflowingBottomBorder?: boolean
  unshiftTop?: boolean
  noBackground?: boolean
}

// Page background gradient (light / dark). There is no shared token for this
// (the V5 `pageBackground` gradient resolved to `none`), so the original V5
// gradient is replicated inline to keep 1:1 rendering.
const PAGE_BACKGROUND_GRADIENT =
  'light-dark(linear-gradient(180deg, transparent 66.99%, rgba(27, 32, 37, 0.04) 100%), linear-gradient(180deg, transparent 66.99%, #FFFFFF08 100%))'

const SectionLayout = ({
  children,
  hasLargerVerticalPadding = false,
  withoutAside = false,
  withOverflowingBottomBorder = false,
  unshiftTop = false,
  noBackground = false,
  className,
  style
}: SectionLayoutProps) => {
  return (
    <div
      className={cn('w-full', className)}
      style={{
        ...(noBackground ? undefined : { background: PAGE_BACKGROUND_GRADIENT }),
        ...style
      }}
      id="section-layout-background"
    >
      <div
        className={cn(
          'mx-auto flex max-w-[2200px] flex-col rounded-[20px] px-[200px]',
          hasLargerVerticalPadding ? 'py-[100px]' : 'py-xl',
          // containerDown('content-xxl', 'content-root') => max-width: 1900px
          withoutAside
            ? '@max-[1900px]/content-root:px-[200px]'
            : '@max-[1900px]/content-root:px-xl',
          // containerDown('content-xl', 'content-root') => max-width: 1420px
          withoutAside
            ? '@max-[1420px]/content-root:px-[120px]'
            : '@max-[1420px]/content-root:px-sm',
          // containerDown('content-l', 'content-root') => max-width: 1189px
          '@max-[1189px]/content-root:px-sm',
          // down('m') => max-width: 1023px
          'max-lg:mb-[60px] max-lg:p-sm',
          unshiftTop && '-mt-md'
        )}
        id="section-layout"
      >
        {children}
      </div>
      {withOverflowingBottomBorder && (
        <div className="bg-background h-md max-sm:h-xl w-full rounded-t-[20px]" />
      )}
    </div>
  )
}
export default memo(SectionLayout)
